import { useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';

import { Box, Fade, Flex, Grid, Spinner, keyframes } from '@chakra-ui/react';
import dayjs from 'dayjs';
import { DailySpinStatus } from 'models';
import routes from 'routes';
import { formatDuration } from 'utils/formatDuration';

import { useLazyCheckSpinQuery } from 'api/contest';

import BurgerButton from 'components/BurgerButton';
import PromoSticker from 'components/home/PromoSticker';
import UserBalance from 'components/profile/UserBalance';

import { useAppSelector } from 'hooks/app';

import { setDailySpinStatus } from 'store/slices/contest';

import { ReactComponent as BoostersIcon } from 'icons/boosters-icon.svg';
import { ReactComponent as FlameIcon } from 'icons/flame.svg';
import { ReactComponent as LogoIcon } from 'icons/logo.svg';
import { ReactComponent as LogoGlowIcon } from 'icons/logo-glow.svg';

import Menu from './Menu';

const hiddenRoutes = [routes.builder, routes.boosters, routes.allBoosters, routes.leaderboard, routes.signIn];

const topLineAnimation = keyframes`
  0% {
    transform: translateY(0);
    top: 0;
  }
  50% {
    top: 50%;
    transform: translateY(-50%);
  }
  100% {
    top: 50%;
    transform: translateY(-50%) rotate(45deg);
  }
`;

const topLineAnimationReverse = keyframes`
  0% {
    
     top: 50%;
    transform: translateY(-50%) rotate(-45deg);
  }
  50% {
    top: 50%;
    transform: translateY(-50%);
  }
  100% {
   transform: translateY(0);
    top: 0;
  }
`;

const bottomLineAnimation = keyframes`
  0% {
    transform: translateY(0);
    bottom: 0;
  }
  50% {
    bottom: 50%;
    transform: translateY(50%);
  }
  100% {
    bottom: 50%;
    transform: translateY(50%) rotate(-45deg);
  }
`;

const bottomLineAnimationReverse = keyframes`
  0% {
    bottom: 50%;
    transform: translateY(50%) rotate(45deg);
  }
  50% {
    bottom: 50%;
    transform: translateY(50%);
  }
  100% {
    transform: translateY(0);
    bottom: 0;
  }
`;

const Header = () => {
  const isAuthicated = useAppSelector((state) => state.auth.isAuthicated);
  const participation = useAppSelector((state) => state.contest.participation);
  const user = useAppSelector((state) => state.auth.user);

  const [checkSpinStatus, { data, isLoading, isFetching }] = useLazyCheckSpinQuery();
  const { pathname } = useLocation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [getNewMessage, setGetNewMessage] = useState(false);

  useEffect(() => {
    if (!data) {
      checkSpinStatus();
    }
  }, []);

  const isHidden = useMemo(() => {
    if (
      (pathname.startsWith(routes.home) && isAuthicated) ||
      hiddenRoutes.some((r) => pathname.startsWith(r)) ||
      (pathname.startsWith(routes.squad) && participation?.status === 'completed') ||
      pathname.startsWith(routes.profile)
    ) {
      return true;
    }
    return false;
  }, [participation, pathname, isAuthicated]);

  const isWide = useMemo(() => {
    if (pathname === '/' && isAuthicated) {
      return true;
    }
    if (participation?.status === 'completed' && !pathname.startsWith(routes.profile)) {
      return true;
    }
    if (pathname.startsWith(routes.leaderboard)) {
      return true;
    }
    return false;
  }, [isAuthicated, pathname]);

  useEffect(() => {
    if (!(window as any).fcSettings) return;
    (window as any).fcSettings = {
      onInit: function () {
        (window as any).fcWidget.on('unreadCount:notify', function (resp: { count: number }) {
          setGetNewMessage(resp.count > 0);
        });
      },
    };
  }, [user]);

  return (
    <Flex flexDirection="column" backgroundColor="#242424" width="100%">
      <Grid
        alignItems="center"
        gridTemplateColumns="1fr auto 1fr"
        gridTemplateRows="1fr auto"
        overflow={isHidden && !isWide ? 'hidden' : 'initial'}
        h={isHidden && !isWide ? '0px' : '48px'}
        zIndex="999999"
        pos="relative"
        px="16px"
      >
        <BurgerButton
          isMenuOpen={isMenuOpen}
          toggleMenu={() => setIsMenuOpen(!isMenuOpen)}
          hasNotification={getNewMessage}
        />

        <>
          <Box as={Link} to={routes.home} pos="relative">
            <LogoIcon style={{ position: 'relative', zIndex: 1 }} />
            <LogoGlowIcon style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', width: '68px', height: '68px', filter: 'blur(30px)' }} />
          </Box>
          <Flex alignItems="center" justifyContent="flex-end" gap="12px" position="relative">
            <Fade
              in={!isMenuOpen}
              style={{
                zIndex: 2,
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
              }}
            >
              {isWide && (
                <>
                  {/* streak counter button */}
                  {/* <StreakButton streakCount={user?.sbcStreakCount} /> */}
                  <BoosterButton spinStatus={data} isLoading={isLoading} isFetching={isFetching} />
                </>
              )}
            </Fade>

            <Fade
              in={isMenuOpen}
              unmountOnExit
              style={{ position: 'absolute', right: '0', top: '50%', transform: 'translateY(-50%)' }}
            >
              <Box />
              {/* <UserBalance user={user} /> */}
            </Fade>
          </Flex>
        </>
      </Grid>
      <Fade
        in={isMenuOpen}
        style={{
          zIndex: 999,
          display: 'grid',
          gridTemplateRows: 'auto ',
          position: 'absolute',
          width: '100%',
          bottom: '0',
          left: '0',
          right: '0',
          top: '0',
          padding: '0 0 20px',
          backgroundColor: '#242424',
          maxWidth: '500px',
          margin: '0 auto',
        }}
        unmountOnExit
      >
        <Menu onClose={() => setIsMenuOpen(false)} user={user} />
        {/* <PromoSticker /> */}
      </Fade>
    </Flex>
  );
};

export default Header;

const shineAnimation = keyframes`
  0% {
    box-shadow: 0 0 15px 8px #F83C3C;
  }
  50% {
    box-shadow: 0 0 15px 0px #F83C3C;
  }
  100% {
    box-shadow: 0 0 15px 8px #F83C3C;
  }
`;

const fadeIdAnimation = keyframes`
  0% {
    opacity: 0;
  }
  100% {
    opacity: 1;
  }
`;

interface BoosterButtonProps {
  spinStatus?: DailySpinStatus;
  isLoading?: boolean;
  isFetching?: boolean;
}

const BoosterButton = ({ spinStatus, isLoading, isFetching }: BoosterButtonProps) => {
  const now = dayjs().tz('America/Los_Angeles');
  const tommorow = dayjs().tz('America/Los_Angeles').add(1, 'day').startOf('day');
  const locksDiff = tommorow.diff(now);
  const durationUntilLocks = dayjs.duration(locksDiff);

  let buttonText = 'soon';
  if (locksDiff > 0) {
    const hours = Math.floor(durationUntilLocks.asHours());
    const minutes = Math.floor(durationUntilLocks.asMinutes() % 60);

    if (hours >= 1) {
      buttonText = `${hours}h`;
    } else {
      buttonText = `${minutes}m`;
    }
  }

  return (
    <Box
      animation={`${spinStatus?.canSpinToday ? shineAnimation + ' 2s ease-in-out infinite' : ''}`}
      position="relative"
      as={Link}
      to={routes.allBoosters}
      w="32px"
      h="32px"
      borderRadius="50%"
    >
      {!spinStatus?.canSpinToday && (
        <Box
          position="absolute"
          top="calc(100% - 9px)"
          left="50%"
          transform="translate(-50%, 0)"
          backgroundColor="#F83C3C"
          borderRadius="20px"
          color="white"
          fontSize="10px"
          w="fit-content"
          whiteSpace="nowrap"
          lineHeight="1.2"
          p="1px 4px"
          fontWeight="700"
          textShadow={`0 -0.5px 0px rgba(0, 0, 0, 0.9)`}
        >
          {!isLoading ? (
            buttonText
          ) : (
            <Spinner
              height="7px"
              width="8px"
              size="xs"
              color="white"
              margin="auto"
              display={isLoading || isFetching ? 'inline-block' : 'none'}
            />
          )}
        </Box>
      )}

      <BoostersIcon />
    </Box>
  );
};

const StreakButton = ({ streakCount }: { streakCount?: number | null }) => {
  return (
    <Flex
      position="relative"
      as={Link}
      to={routes.streakPromo}
      w="32px"
      h="32px"
      backgroundColor="#FF0000"
      borderRadius="50%"
      justifyContent="center"
      alignItems="center"
    >
      <Box
        as={FlameIcon}
        fill="white"
        w="13px"
        __css={{
          path: {
            fill: 'white !important',
          },
        }}
      />
      <Flex
        position="absolute"
        top="calc(100% - 9px)"
        left="50%"
        transform="translate(-50%, 0)"
        backgroundColor="#F83C3C"
        borderRadius="20px"
        color="white"
        fontSize="10px"
        w="fit-content"
        whiteSpace="nowrap"
        lineHeight="1.2"
        p="1px 4px"
        fontWeight="700"
        textShadow={`0 -0.5px 0px rgba(0, 0, 0, 0.9)`}
      >
        {streakCount}/5
      </Flex>
    </Flex>
  );
};
